import { useCallback } from 'react';
import { useUIStore } from '../stores/uiStore';
import { ModalName } from '../types/store';

/**
 * Custom hook for modal state management
 * Provides a clean API for components to control modals
 */
export const useModal = (modalName: ModalName) => {
  const { modals, openModal, closeModal, toggleModal } = useUIStore();
  
  // Stabilize function references to prevent infinite loops in useEffect
  const open = useCallback(() => openModal(modalName), [openModal, modalName]);
  const close = useCallback(() => closeModal(modalName), [closeModal, modalName]);
  const toggle = useCallback(() => toggleModal(modalName), [toggleModal, modalName]);
  
  return {
    isOpen: modals[modalName],
    open,
    close,
    toggle,
  };
};

/**
 * Hook to get all modal states and actions
 * Useful for components that need to manage multiple modals
 */
export const useModals = () => {
  const { modals, openModal, closeModal, closeAllModals, toggleModal } = useUIStore();
  
  // Stabilize function references
  const stableOpenModal = useCallback(
    (modalName: ModalName) => openModal(modalName),
    [openModal]
  );
  
  const stableCloseModal = useCallback(
    (modalName: ModalName) => closeModal(modalName),
    [closeModal]
  );
  
  const stableCloseAllModals = useCallback(
    () => closeAllModals(),
    [closeAllModals]
  );
  
  const stableToggleModal = useCallback(
    (modalName: ModalName) => toggleModal(modalName),
    [toggleModal]
  );
  
  return {
    modals,
    openModal: stableOpenModal,
    closeModal: stableCloseModal,
    closeAllModals: stableCloseAllModals,
    toggleModal: stableToggleModal,
  };
};
